/*
 * Brents home: panel-by-panel scroll (desktop only).
 *
 * The home sections are already a sticky "cover" stack in CSS: each panel pins below the
 * top bar and the next one slides up over it. This script makes that motion discrete, the
 * way miumiu.com's vertical Swiper does it: one wheel gesture or key press moves exactly
 * one panel, tweened over PANEL_DURATION with the `ease` curve, and further wheel events
 * (trackpad inertia) are swallowed until a new gesture starts.
 *
 * Native scrolling stays in place underneath. The script only decides *where* the page
 * scrolls to and animates window.scrollTo, so anchors, the theme editor, and browsers
 * without JS keep working. Panels taller than the viewport (short windows, the footer)
 * scroll natively inside themselves until their edge, then the next gesture flips.
 *
 * Tuning constants are at the top; there are deliberately no theme settings for them.
 */
(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 990px)';
  var PANEL_DURATION = 500; // ms, miumiu: Swiper speed 500
  var PANEL_EASE = [0.25, 0.1, 0.25, 1]; // CSS `ease`
  var WHEEL_MIN_DELTA = 10; // ignore tiny deltas (Swiper thresholdDelta)
  var WHEEL_COOLDOWN = 800; // ms after a flip before another can start (Swiper thresholdTime)
  var WHEEL_GESTURE_GAP = 150; // ms: a same-direction, non-growing delta sooner than this is inertia (Swiper)
  var SETTLE_DELAY = 150; // ms of scroll silence before snapping an off-grid position
  var FIT_TOLERANCE = 8; // px: a panel this close to the viewport height counts as fitting it

  var mq = window.matchMedia(DESKTOP_QUERY);
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var panels = []; // [{ el, top, height, counters: [] }]
  var current = 0;
  var topBar = 0;
  var avail = 0; // viewport height below the top bar
  var animating = false;
  var animFrame = 0;
  var lastFlipTime = 0;
  var lastWheel = null; // { time, delta, direction }
  var wheelNative = false; // current gesture is scrolling natively inside a tall panel
  var settleTimer = 0;
  var lastSetY = -1; // last position we scrolled to ourselves
  var enabled = false;

  /* ---------- measuring ---------- */

  function cssLength(value) {
    value = (value || '').trim();
    if (!value) return 0;
    var n = parseFloat(value);
    if (value.slice(-3) === 'rem') return n * parseFloat(getComputedStyle(document.documentElement).fontSize);
    if (value.slice(-2) === 'em') return n * parseFloat(getComputedStyle(document.body).fontSize);
    return n; // px
  }

  /* Document-space top of an element ignoring any sticky offset currently applied to it or
     its siblings: walk up from the nearest non-sticky ancestor and add preceding siblings'
     outer heights. offsetTop/getBoundingClientRect report the *stuck* position in some
     browsers, which would drift the targets. */
  function layoutTop(el) {
    var parent = el.parentElement;
    var y = parent.getBoundingClientRect().top + window.scrollY;
    var pcs = getComputedStyle(parent);
    y += parseFloat(pcs.borderTopWidth) + parseFloat(pcs.paddingTop);
    var child = parent.firstElementChild;
    while (child && child !== el) {
      var ccs = getComputedStyle(child);
      if (ccs.display !== 'none' && ccs.position !== 'absolute' && ccs.position !== 'fixed') {
        y += child.offsetHeight + parseFloat(ccs.marginTop) + parseFloat(ccs.marginBottom);
      }
      child = child.nextElementSibling;
    }
    y += parseFloat(getComputedStyle(el).marginTop);
    return y;
  }

  function collectPanels() {
    var els = [];
    var main = document.querySelector('main');
    if (!main) return els;
    Array.prototype.forEach.call(main.children, function (child) {
      if (child.classList.contains('shopify-section') && child.offsetHeight > 0) els.push(child);
    });
    var footer = document.querySelector('.brents-footer-panel--home');
    if (footer && footer.offsetHeight > 0) els.push(footer);
    return els;
  }

  function measure() {
    topBar = cssLength(getComputedStyle(document.documentElement).getPropertyValue('--brents-top-bar'));
    avail = window.innerHeight - topBar;
    var els = collectPanels();
    panels = els.map(function (el) {
      return {
        el: el,
        top: Math.max(0, Math.round(layoutTop(el) - topBar)),
        height: el.offsetHeight,
        counters: el.querySelectorAll('[data-brents-panel-counter], .editorial-product__counter')
      };
    });
    var maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    panels.forEach(function (p) {
      p.top = Math.min(p.top, maxScroll);
      // how far native scrolling may go inside this panel before it is "at its end"
      var overflow = p.height - avail;
      p.end = Math.min(p.top + (overflow > FIT_TOLERANCE ? overflow : 0), maxScroll);
    });
    current = indexAt(window.scrollY);
    updateCounters();
  }

  function indexAt(y) {
    var i = 0;
    for (var k = 0; k < panels.length; k++) if (panels[k].top <= y + 1) i = k;
    return i;
  }

  function onGrid(y) {
    var p = panels[current];
    return p && y >= p.top - 1 && y <= p.end + 1;
  }

  /* ---------- counters ("2/4") ---------- */

  function updateCounters() {
    var total = panels.length;
    panels.forEach(function (p, i) {
      Array.prototype.forEach.call(p.counters, function (c) {
        c.textContent = i + 1 + '/' + total;
      });
    });
  }

  /* ---------- tween ---------- */

  function bezier(p1x, p1y, p2x, p2y) {
    function A(a1, a2) { return 1 - 3 * a2 + 3 * a1; }
    function B(a1, a2) { return 3 * a2 - 6 * a1; }
    function C(a1) { return 3 * a1; }
    function calc(t, a1, a2) { return ((A(a1, a2) * t + B(a1, a2)) * t + C(a1)) * t; }
    function slope(t, a1, a2) { return 3 * A(a1, a2) * t * t + 2 * B(a1, a2) * t + C(a1); }
    return function (x) {
      var t = x;
      for (var i = 0; i < 8; i++) {
        var s = slope(t, p1x, p2x);
        if (s === 0) break;
        t -= (calc(t, p1x, p2x) - x) / s;
      }
      return calc(t, p1y, p2y);
    };
  }
  var ease = bezier(PANEL_EASE[0], PANEL_EASE[1], PANEL_EASE[2], PANEL_EASE[3]);

  function scrollTo(y) {
    lastSetY = Math.round(y);
    window.scrollTo(0, y);
  }

  function goTo(index, opts) {
    opts = opts || {};
    index = Math.max(0, Math.min(panels.length - 1, index));
    if (!panels.length) return;
    var target = opts.toEnd ? panels[index].end : panels[index].top;
    var from = window.scrollY;
    cancelAnimationFrame(animFrame);
    current = index;
    wheelNative = false;
    lastFlipTime = performance.now(); // cooldown counts from the trigger, as in Swiper
    updateCounters();
    document.dispatchEvent(new CustomEvent('brents:panelchange', { detail: { index: index, panel: panels[index].el } }));

    var duration = reduceMotion.matches || opts.instant ? 0 : PANEL_DURATION;
    if (duration === 0 || Math.abs(target - from) < 1) {
      scrollTo(target);
      return;
    }
    animating = true;
    document.documentElement.classList.add('brents-panels--animating');
    var start = performance.now();
    function frame(now) {
      var t = Math.min(1, (now - start) / duration);
      scrollTo(from + (target - from) * ease(t));
      if (t < 1) {
        animFrame = requestAnimationFrame(frame);
      } else {
        animating = false;
        document.documentElement.classList.remove('brents-panels--animating');
      }
    }
    animFrame = requestAnimationFrame(frame);
  }

  function next() { if (current < panels.length - 1) goTo(current + 1); }
  function prev() { if (current > 0) goTo(current - 1); }

  /* ---------- input: wheel ---------- */

  function uiBlocksScroll(target) {
    if (/overflow-hidden/.test(document.body.className)) return true; // Dawn drawers / modals
    return !!(target && target.closest && target.closest('cart-drawer, modal-dialog, menu-drawer, header-drawer, details[open] > .menu-drawer, .brents-drawer, [role="dialog"]'));
  }

  function onWheel(e) {
    if (!enabled || uiBlocksScroll(e.target)) return;
    var delta = e.deltaY;
    if (e.deltaMode === 1) delta *= 16;
    else if (e.deltaMode === 2) delta *= window.innerHeight;
    if (delta === 0 || Math.abs(e.deltaX) > Math.abs(delta)) return; // horizontal gesture: not ours

    // Record every event, as Swiper's mousewheel module does, so inertia can be told apart
    // from a new gesture even while we are animating or cooling down.
    var now = performance.now();
    var dir = delta > 0 ? 1 : -1;
    var evt = { time: now, delta: Math.abs(delta), direction: dir };
    var prevEvt = lastWheel;
    lastWheel = evt;
    var newGesture = !prevEvt || evt.direction !== prevEvt.direction || evt.delta > prevEvt.delta || evt.time > prevEvt.time + WHEEL_GESTURE_GAP;

    if (animating) { e.preventDefault(); return; }

    var p = panels[current];
    var y = window.scrollY;

    // Tall panel: scroll natively inside it until its edge (miumiu: releaseOnEdges), but only
    // for a gesture that started here. The inertia tail of the flip that landed on this panel
    // is swallowed so a flick lands on the panel's top, not somewhere inside it.
    if (p && ((dir > 0 && y < p.end - 1) || (dir < 0 && y > p.top + 1))) {
      if (newGesture) wheelNative = true;
      if (!wheelNative) { e.preventDefault(); return; }
      // Clamp so native scrolling never runs past the panel edge into the next panel.
      var maxStep = dir > 0 ? p.end - y : y - p.top;
      if (evt.delta > maxStep) {
        e.preventDefault();
        scrollTo(y + dir * maxStep);
        lastFlipTime = now; // arriving at an edge counts like a flip: wait for a new gesture
      }
      return;
    }

    e.preventDefault();
    wheelNative = false;
    if (!newGesture) return;
    if (evt.delta < WHEEL_MIN_DELTA) return;
    if (now - lastFlipTime < WHEEL_COOLDOWN) return;

    if (dir > 0) next(); else prev();
  }

  /* ---------- input: keys ---------- */

  function onKey(e) {
    if (!enabled || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
    var t = e.target;
    if (t && (t.closest('input, textarea, select, [contenteditable="true"]') || uiBlocksScroll(t))) return;
    var handled = true;
    switch (e.key) {
      case 'ArrowDown':
      case 'PageDown':
        next();
        break;
      case ' ':
        if (e.shiftKey) prev(); else next();
        break;
      case 'ArrowUp':
      case 'PageUp':
        prev();
        break;
      case 'Home':
        goTo(0);
        break;
      case 'End':
        goTo(panels.length - 1);
        break;
      default:
        handled = false;
    }
    if (handled) e.preventDefault();
  }

  /* ---------- input: everything else (scrollbar, focus, anchors, back/forward) ---------- */

  function onScroll() {
    if (!enabled) return;
    if (animating || Math.abs(window.scrollY - lastSetY) < 1) return; // our own scroll
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      var y = window.scrollY;
      var i = indexAt(y);
      current = i;
      updateCounters();
      if (!onGrid(y)) goTo(i);
    }, SETTLE_DELAY);
  }

  /* ---------- theme editor ---------- */

  function onSectionSelect(e) {
    if (!enabled) return;
    measure();
    var idx = panels.findIndex(function (p) { return p.el === e.target || p.el.contains(e.target); });
    if (idx >= 0) goTo(idx, { instant: true });
  }

  function onSectionChange() {
    if (!enabled) return;
    measure();
  }

  /* ---------- lifecycle ---------- */

  var resizeTimer = 0;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (!mq.matches) { disable(); return; }
      if (!enabled) { enable(); return; }
      var i = current;
      measure();
      goTo(i, { instant: true });
    }, 100);
  }

  function enable() {
    if (enabled) return;
    enabled = true;
    document.documentElement.classList.add('brents-panels');
    measure();
    if (!onGrid(window.scrollY)) goTo(current, { instant: true });
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('shopify:section:select', onSectionSelect);
    document.addEventListener('shopify:section:load', onSectionChange);
    document.addEventListener('shopify:section:unload', onSectionChange);
    document.addEventListener('shopify:section:reorder', onSectionChange);
  }

  function disable() {
    if (!enabled) return;
    enabled = false;
    cancelAnimationFrame(animFrame);
    animating = false;
    document.documentElement.classList.remove('brents-panels', 'brents-panels--animating');
    window.removeEventListener('wheel', onWheel);
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('shopify:section:select', onSectionSelect);
    document.removeEventListener('shopify:section:load', onSectionChange);
    document.removeEventListener('shopify:section:unload', onSectionChange);
    document.removeEventListener('shopify:section:reorder', onSectionChange);
  }

  function init() {
    if (mq.matches) enable();
    window.addEventListener('resize', onResize);
    window.addEventListener('load', function () { if (enabled) measure(); });

    // Hero "Scroll to explore" → next panel
    document.addEventListener('click', function (e) {
      var hint = e.target.closest && e.target.closest('.brents-hero .banner__text');
      if (hint && enabled) { e.preventDefault(); next(); }
    });

    // exposed for dial-in from the console: BrentsPanels.goTo(2)
    window.BrentsPanels = { goTo: goTo, next: next, prev: prev, measure: measure, panels: function () { return panels; } };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
